import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Pagination,
  useMediaQuery,
} from "@mui/material";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { useParams, Outlet, useLocation } from "react-router-dom";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import {
  fetchAllGateVisitors,
  fetchGateVisitorStatuses,
  fetchGateVisitorTypes,
} from "./Utils/gateVisitors.api";
import { getStatusInfo } from "./Utils/gateVisitors.utils";
import GateVisitorsCards from "./gateVisitorsCards";
import GateVisitorsStatusChips from "./StatusFilterCards";
import api  from "../../utils/api";

const GateVisitorsPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [visitors, setVisitors] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [types, setTypes] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 18;
  const [selectedVisitorTypes, setSelectedVisitorTypes] = useState([]);
  const [smartFilterEnabled, setSmartFilterEnabled] = useState(false);

  // Helper needed before applyFilters
  const getVisitorTypeName = (typeId) => {
    const found = types.find((t) => Number(t.TypeID) === Number(typeId));
    return found?.TypeName || "Unknown Type";
  };

  const applyFilters = () => {
    let results = [...visitors];
    const terms = searchTerm
      .toLowerCase()
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (terms.length) {
      results = results.filter((v) => {
        const typeName = getVisitorTypeName(v.VisitorTypeID).toLowerCase();
        const statusText = (v.StatusName || "Unknown").toLowerCase();
        const visitorName = `${v.FirstName || ""} ${v.LastName || ""}`.toLowerCase();
        const memberName = `${v.MemberFirstName || ""} ${v.MemberLastName || ""}`.toLowerCase();
        const memberAddress = (v.MemberAddress || "").toLowerCase();
        const combined = `${v.VisitID} ${v.ClubMemberID} ${typeName} ${statusText} ${visitorName} ${memberName} ${memberAddress}`;
        return terms.some((term) => combined.includes(term));
      });
    }

    if (filterStatuses.length) {
      results = results.filter((v) =>
        filterStatuses.includes((v.StatusName || "Unknown"))
      );
    }

    if (startDate || endDate) {
      results = results.filter((v) => {
        const visitDate = new Date(v.ArrivalDate);
        if (startDate && visitDate < new Date(startDate)) return false;
        if (endDate && visitDate > new Date(endDate)) return false;
        return true;
      });
    }

    if (selectedVisitorTypes.length) {
      results = results.filter((v) =>
        selectedVisitorTypes.includes(v.VisitorTypeID)
      );
    }

    if (smartFilterEnabled) {
      const today = new Date().toDateString();
      results = results.filter((v) => {
        const isToday = new Date(v.ArrivalDate).toDateString() === today;
        const isUnknown = (v.StatusName || "Unknown").toLowerCase() === "unknown";
        return isToday && isUnknown;
      });
    }

    setFiltered(results);
  };

  const loadData = async () => {
    try {
      const [resVisitors, resStatuses, resTypes] = await Promise.all([
        fetchAllGateVisitors(),
        fetchGateVisitorStatuses(),
        fetchGateVisitorTypes(),
      ]);
  
      const allVisitors = resVisitors?.data?.data || resVisitors?.data || [];
      const allStatuses = resStatuses?.data || [];
      const allTypes = resTypes || [];
  
      const memberIDs = [...new Set(allVisitors.map((v) => v.ClubMemberID).filter(Boolean))];
      const memberMap = {};
  
      await Promise.all(
        memberIDs.map(async (id) => {
          try {
            const res = await api.get(`/members/${encodeURIComponent(id)}`);
            memberMap[id] = res.data; // Axios: use `res.data` directly
          } catch (err) {
            console.error(`Error fetching member info for ID ${id}`, err);
          }
        })
      );
  
      const enrichedVisitors = allVisitors.map((v) => {
        const m = memberMap[v.ClubMemberID] || {};
        return {
          ...v,
          MemberFirstName: m.FirstName || "",
          MemberLastName: m.LastName || "",
          MemberAddress: m.FullAddress || "",
        };
      });
  
      setVisitors(enrichedVisitors);
      setStatuses(allStatuses);
      setTypes(allTypes);
  
      const counts = {};
      for (const v of enrichedVisitors) {
        const s = (v.StatusName || "Unknown").toLowerCase();
        counts[s] = (counts[s] || 0) + 1;
      }
      setStatusCounts(counts);
    } catch (error) {
      console.error("Error loading gate visitor data:", error);
    }
  };
  

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (location?.state?.reload) {
      loadData();
      window.history.replaceState({}, document.title);
    }
  }, [location?.state?.reload]);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, filterStatuses, startDate, endDate, visitors, selectedVisitorTypes, smartFilterEnabled]);

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterStatuses([]);
    setStartDate(null);
    setEndDate(null);
    setSelectedVisitorTypes([]);
    setSmartFilterEnabled(false);
    setPage(1);
  };

  const handleStatusToggle = (status) => {
    setFilterStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleVisitorTypeToggle = (typeId) => {
    setSelectedVisitorTypes((prev) =>
      prev.includes(typeId)
        ? prev.filter((id) => id !== typeId)
        : [...prev, typeId]
    );
  };

  if (id) return <Outlet />;

  const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: "100%", height: "100%", boxSizing: "border-box", overflow: "auto", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">Gate Visitors</Typography>
        <Box sx={{ display: "flex", flexDirection: "row", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" startIcon={<ViewModuleIcon />} disabled>Card View</Button>
          <Button variant="outlined" color="secondary" onClick={handleResetFilters}>
            Reset Filters
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mb: 3 }}>
        <TextField fullWidth placeholder="Search by ID, Member, Address, or Member Name" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker label="Start Date" value={startDate} onChange={(d) => setStartDate(d)} slotProps={{ textField: { fullWidth: true } }} />
          <DatePicker label="End Date" value={endDate} onChange={(d) => setEndDate(d)} slotProps={{ textField: { fullWidth: true } }} />
        </LocalizationProvider>
      </Box>

      {/* Filter Chips */}
      <GateVisitorsStatusChips
        statuses={statuses}
        statusCounts={statusCounts}
        activeFilters={filterStatuses}
        onToggle={handleStatusToggle}
        visitorTypes={types}
        selectedVisitorTypes={selectedVisitorTypes}
        onVisitorTypeToggle={handleVisitorTypeToggle}
        smartFilterEnabled={smartFilterEnabled}
        onToggleSmartFilter={() => setSmartFilterEnabled(prev => !prev)}
      />

      {/* Visitor Cards */}
      <Box sx={{ flexGrow: 1 }}>
      <GateVisitorsCards
  visits={paginated}
  getStatusDetails={(status) => getStatusInfo(statuses, status)}
  onRefresh={loadData}
/>

      </Box>

      {/* Pagination */}
      {filtered.length > itemsPerPage && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={Math.ceil(filtered.length / itemsPerPage)}
            page={page}
            onChange={(e, val) => setPage(val)}
          />
        </Box>
      )}
    </Box>
  );
};

export default GateVisitorsPage;
